import { RecentlyViewed } from '@theme/recently-viewed-products';
import { formatMoney } from '@theme/money-formatting';

const WISHLIST_KEY = 'qlWishlistProductHandles';
const WISHLIST_EVENT = 'ql:wishlist-change';

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const readWishlist = () => {
  try {
    const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(value) ? value.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const writeWishlist = (handles) => {
  const uniqueHandles = [...new Set(handles.filter(Boolean))];
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(uniqueHandles));
  window.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { handles: uniqueHandles } }));
};

const updateWishlistButtons = () => {
  const saved = new Set(readWishlist());
  document.querySelectorAll('[data-ql-wishlist-toggle]').forEach((button) => {
    const active = saved.has(button.dataset.productHandle || '');
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Retirer des favoris' : 'Ajouter aux favoris');
  });

  document.querySelectorAll('[data-ql-header-wishlist-count]').forEach((count) => {
    count.textContent = String(saved.size);
    count.hidden = saved.size === 0;
  });
};

const animateWishlistFlight = (button) => {
  button.classList.remove('is-wish-pulsing');
  void button.offsetWidth;
  button.classList.add('is-wish-pulsing');

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const target = [...document.querySelectorAll('[data-ql-header-wishlist]')].find((candidate) => {
    const rectangle = candidate.getBoundingClientRect();
    const styles = window.getComputedStyle(candidate);
    return rectangle.width > 0 && rectangle.height > 0 && styles.visibility !== 'hidden';
  });
  if (!target) return;

  const start = button.getBoundingClientRect();
  const destination = target.getBoundingClientRect();
  const flight = document.createElement('span');
  flight.className = 'ql-wishlist-flight';
  flight.setAttribute('aria-hidden', 'true');
  flight.innerHTML = '<svg viewBox="0 0 24 24"><path d="M20.4 5.9a5.1 5.1 0 0 0-7.2 0L12 7.1l-1.2-1.2a5.1 5.1 0 0 0-7.2 7.2L12 21l8.4-7.9a5.1 5.1 0 0 0 0-7.2Z"></path></svg>';
  flight.style.left = `${start.left + start.width / 2 - 12}px`;
  flight.style.top = `${start.top + start.height / 2 - 12}px`;
  flight.style.setProperty('--ql-flight-x', `${destination.left + destination.width / 2 - start.left - start.width / 2}px`);
  flight.style.setProperty('--ql-flight-y', `${destination.top + destination.height / 2 - start.top - start.height / 2}px`);
  document.body.append(flight);

  requestAnimationFrame(() => flight.classList.add('is-flying'));
  window.setTimeout(() => {
    flight.remove();
    target.classList.remove('is-receiving-wish');
    void target.offsetWidth;
    target.classList.add('is-receiving-wish');
  }, 720);
};

const initializeDepthMotion = (root) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let animationFrame = 0;

  const update = () => {
    animationFrame = 0;
    const rectangle = root.getBoundingClientRect();
    const travel = window.innerHeight + rectangle.height;
    const progress = Math.max(-1, Math.min(1, (window.innerHeight - rectangle.top) / travel * 2 - 1));
    root.style.setProperty('--ql-showcase-scroll', progress.toFixed(4));
    root.style.setProperty('--ql-depth-rose-x', `${(progress * 34).toFixed(2)}px`);
    root.style.setProperty('--ql-depth-rose-y', `${(progress * -52).toFixed(2)}px`);
    root.style.setProperty('--ql-depth-ivory-x', `${(progress * -42).toFixed(2)}px`);
    root.style.setProperty('--ql-depth-ivory-y', `${(progress * 62).toFixed(2)}px`);
    root.style.setProperty('--ql-depth-blush-x', `${(progress * 24).toFixed(2)}px`);
    root.style.setProperty('--ql-depth-blush-y', `${(progress * -38).toFixed(2)}px`);
  };

  const requestUpdate = () => {
    if (animationFrame) return;
    animationFrame = requestAnimationFrame(update);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  update();
};

const productImageUrl = (image, width = 720) => {
  const source = typeof image === 'string' ? image : image?.src || '';
  if (!source) return '';

  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return source;
  }
};

const productPrice = (value, root) => {
  const moneyFormat = root?.dataset.moneyFormat || '${{amount}}';
  const currency = root?.dataset.currency || 'TND';
  return formatMoney(Number(value || 0), moneyFormat, currency);
};

const createCardMarkup = (product, root) => {
  const images = Array.isArray(product.images) ? product.images : [];
  const primary = productImageUrl(product.featured_image || images[0]);
  const secondary = productImageUrl(images[1]);
  const handle = escapeHtml(product.handle);
  const title = escapeHtml(product.title);
  const productUrl = escapeHtml(product.url || `/products/${product.handle}`);
  const compareAtPrice = Number(product.compare_at_price || 0);
  const price = Number(product.price || 0);
  const soldOut = product.available === false;

  return `
    <article class="ql-luxury-card" data-ql-luxury-card data-product-id="${escapeHtml(product.id)}" data-product-handle="${handle}">
      <div class="ql-luxury-card__frame">
        <a class="ql-luxury-card__media" href="${productUrl}" aria-label="${title}" data-ql-card-media-link${secondary ? ' data-has-secondary-image="true"' : ''}>
          ${
            primary
              ? `<img class="ql-luxury-card__image ql-luxury-card__image--primary" src="${escapeHtml(primary)}" alt="${title}" loading="lazy" width="720" height="960">`
              : '<span class="ql-luxury-card__empty-image" aria-hidden="true"></span>'
          }
          ${
            secondary
              ? `<img class="ql-luxury-card__image ql-luxury-card__image--secondary" src="${escapeHtml(secondary)}" alt="${title}" loading="lazy" width="720" height="960">`
              : ''
          }
          ${soldOut ? '<span class="ql-luxury-card__status">Épuisé</span>' : ''}
        </a>
        <button class="ql-luxury-card__wish" type="button" aria-label="Ajouter aux favoris" aria-pressed="false" data-ql-wishlist-toggle data-product-handle="${handle}" data-product-title="${title}">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.4 5.9a5.1 5.1 0 0 0-7.2 0L12 7.1l-1.2-1.2a5.1 5.1 0 0 0-7.2 7.2L12 21l8.4-7.9a5.1 5.1 0 0 0 0-7.2Z"></path></svg>
        </button>
      </div>
      <div class="ql-luxury-card__details">
        <a class="ql-luxury-card__title" href="${productUrl}">${title}</a>
        <div class="ql-luxury-card__price">
          ${compareAtPrice > price ? `<s>${escapeHtml(productPrice(compareAtPrice, root))}</s>` : ''}
          <span>${escapeHtml(productPrice(price, root))}</span>
        </div>
      </div>
    </article>
  `;
};

const fetchProduct = async (handle, root) => {
  const rootUrl = (root?.dataset.rootUrl || '').replace(/\/$/, '');
  const response = await fetch(`${rootUrl}/products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Unable to load product ${handle}`);
  return response.json();
};

const initializeCarousel = (root) => {
  const track = root.querySelector('[data-ql-showcase-track]');
  const previous = root.querySelector('[data-ql-showcase-prev]');
  const next = root.querySelector('[data-ql-showcase-next]');
  if (!track || !previous || !next) return;

  const scrollStep = () => {
    const card = track.querySelector('.ql-luxury-card');
    const styles = window.getComputedStyle(track);
    return (card?.getBoundingClientRect().width || track.clientWidth * 0.5) + Number.parseFloat(styles.columnGap || '0');
  };

  const update = () => {
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth - 2);
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maximum;
    root.classList.toggle('has-overflow', maximum > 2);
  };

  previous.addEventListener('click', () => track.scrollBy({ left: -scrollStep(), behavior: 'smooth' }));
  next.addEventListener('click', () => track.scrollBy({ left: scrollStep(), behavior: 'smooth' }));
  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  requestAnimationFrame(update);
};

const initializeRecentlyViewed = async (root) => {
  const region = root.querySelector('[data-ql-recently-viewed]');
  const list = root.querySelector('[data-ql-recently-viewed-list]');
  if (!region || !list) return;

  const handle = RecentlyViewed.getProductHandles()[0];
  if (!handle) {
    region.hidden = list.children.length === 0;
    updateWishlistButtons();
    return;
  }

  try {
    const product = await fetchProduct(handle, root);
    list.innerHTML = createCardMarkup(product, root);
    region.hidden = false;
    updateWishlistButtons();
  } catch {
    region.hidden = list.children.length === 0;
  }
};

const renderWishlist = async (root) => {
  const grid = root.querySelector('[data-ql-wishlist-grid]');
  const empty = root.querySelector('[data-ql-wishlist-empty]');
  const loading = root.querySelector('[data-ql-wishlist-loading]');
  const count = root.querySelector('[data-ql-wishlist-count]');
  if (!grid || !empty || !loading) return;

  const handles = readWishlist();
  loading.hidden = false;
  grid.hidden = true;
  empty.hidden = true;

  if (!handles.length) {
    loading.hidden = true;
    empty.hidden = false;
    if (count) count.textContent = '0';
    return;
  }

  const products = await Promise.all(
    handles.map((handle) => fetchProduct(handle, root).catch(() => null))
  );
  const availableProducts = products.filter(Boolean);

  loading.hidden = true;
  if (!availableProducts.length) {
    empty.hidden = false;
    writeWishlist([]);
    return;
  }

  grid.innerHTML = availableProducts.map((product) => createCardMarkup(product, root)).join('');
  grid.hidden = false;
  if (count) count.textContent = String(availableProducts.length);
  updateWishlistButtons();
};

const initializeRoots = (scope = document) => {
  scope.querySelectorAll('[data-ql-product-showcase]').forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    initializeCarousel(root);
    initializeRecentlyViewed(root);
    initializeDepthMotion(root);
  });

  scope.querySelectorAll('[data-ql-wishlist-page]').forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    renderWishlist(root);
  });

  updateWishlistButtons();
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const wishlistButton = event.target.closest('[data-ql-wishlist-toggle]');
  if (wishlistButton) {
    event.preventDefault();
    event.stopPropagation();
    const handle = wishlistButton.dataset.productHandle;
    if (!handle) return;

    const handles = readWishlist();
    const isAdding = !handles.includes(handle);
    writeWishlist(isAdding ? [handle, ...handles] : handles.filter((item) => item !== handle));
    if (isAdding) animateWishlistFlight(wishlistButton);
    return;
  }

  const mediaLink = event.target.closest('[data-ql-card-media-link][data-has-secondary-image]');
  if (!mediaLink || !window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

  const card = mediaLink.closest('[data-ql-luxury-card]');
  if (!card || card.classList.contains('is-previewing')) return;
  event.preventDefault();
  card.classList.add('is-previewing');
});

window.addEventListener(WISHLIST_EVENT, () => {
  updateWishlistButtons();
  document.querySelectorAll('[data-ql-wishlist-page]').forEach((root) => renderWishlist(root));
});

document.addEventListener('shopify:section:load', (event) => initializeRoots(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeRoots());
} else {
  initializeRoots();
}
